import { useEffect, useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { Link, Navigate } from '../lib/routing';
import { hasUnsavedChanges } from '../lib/unsaved-changes';
import { useAuth } from '../hooks/use-auth';
import { useRoles, ROLE_LABEL, type Role } from '../hooks/use-roles';
import FullPageSpinner from './full-page-spinner';

/**
 * Guards a route to a single active-role space (/teacher/*, /direction/*,
 * /tutor/*). Reuses useRoles() — the same table-driven source AdminRoute and
 * the sidebar switcher already read — instead of a second role check. Real
 * authorization stays server-side (staffProcedure/directionProcedure/…); this
 * only stops a wrong-mode user from navigating into a page whose UI assumes
 * a role it won't have.
 *
 * Also resyncs activeRole to the route's role: a multi-role user typing a
 * /teacher/... URL while activeRole is still 'tutor' (last manual pick) must
 * not leave the sidebar stuck on the previous mode.
 */
const MODE_NAME: Record<Role, string> = { ...ROLE_LABEL, tutor: 'Parent' };

function AccessReserved() {
  return (
    <main className="flex flex-col items-center justify-center gap-4 min-h-[100dvh] px-6 text-center bg-background">
      <h1 className="font-black text-[24px] text-foreground m-0">Accès réservé</h1>
      <p className="font-medium text-[15px] text-muted-foreground max-w-[320px] m-0">
        Cette page n'est pas disponible avec ton compte.
      </p>
      <Link to="/app" className="inline-flex items-center justify-center min-h-[44px] px-5 rounded-full bg-naia-navy text-card font-extrabold text-[15px] no-underline">
        Retour à mon accueil
      </Link>
    </main>
  );
}

export default function RoleRoute({ role, children }: { role: Role; children: ReactNode }) {
  const { session, loading: authLoading } = useAuth();
  const { loading: rolesLoading, available, activeRole, setActiveRole } = useRoles();

  /* Read via ref, not as an effect dep: the manual switcher sets activeRole
     then navigates away (async — TanStack's navigate doesn't commit the new
     route synchronously). This route stays mounted for that gap; if the
     effect re-ran on every activeRole change it would see the just-picked
     role mismatch its own (still current) `role` and instantly revert the
     switch before the navigation lands. Resync only needs to run once per
     route mount/identity — the direct-URL-navigation case this guards. */
  const activeRoleRef = useRef(activeRole);
  activeRoleRef.current = activeRole;
  const [declined, setDeclined] = useState(false);

  useEffect(() => {
    if (rolesLoading) return;
    if (activeRoleRef.current === role || !available.includes(role)) return;
    if (hasUnsavedChanges() && !window.confirm(`Passer en mode ${MODE_NAME[role]} ? Les modifications non enregistrées seront perdues.`)) {
      setDeclined(true);
      return;
    }
    setActiveRole(role);
    toast.info(`Passage en mode ${MODE_NAME[role]}`);
  }, [rolesLoading, available, role, setActiveRole]);

  if (authLoading || rolesLoading) return <FullPageSpinner />;
  if (!session) return <Navigate to="/sign-in" replace />;
  if (!available.includes(role)) return <AccessReserved />;
  if (declined) return <Navigate to="/app" replace />;
  if (activeRole !== role) return <FullPageSpinner />;

  return <>{children}</>;
}
